// General journals (§2 /journals) — live parity: "Double-entry journal — debit one account,
// credit another." Capture form (Date·Ref·Amount·tax·DEBIT gl+G/D/C/V·CREDIT gl+G/D/C/V·descs·
// toggles·Add) → staged table (DATE·DEBIT·CREDIT·DESCRIPTION·REF·AMOUNT·× + Total) → Post to Ledger.
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { rowDelete } from '../shared/confirm.js';
import { dropdownKeydown } from '../shared/dropnav.js';
import { searchSelect } from '../shared/searchselect.js';
import { tenantShell } from './tshell.js';
import { formatGlCode, formatDate, fmtR, fmtRS, incrementRef, controlTag, projectedBalance } from './bank_helpers.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import { companyRole, isCompanyAdmin } from '../shared/companyrole.js';
import { mountFloatRoot, closeFloatRoot } from '../shared/floatroot.js';
import { t } from '../shared/i18n.js';

// The local date, as governance.js builds it. toISOString() gives the UTC date: in Cape Town (UTC+2) before 02:00
// "today" was still yesterday, so a new journal entry started on the day before (2026-10-09).
const isoLocal = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function journalsScreen(companyId){
  // Owners/Admins may date a journal in an EARLIER financial year (owner 2026-09-28); a normal user
  // stays in the current one. Their date fields carry data-past; the API enforces the same rule.
  const coRole = companyRole(companyId);
  const reference = signal(null);
  const rows = signal([]);
  const loading = signal(true), err = signal(''), notice = signal('');

  const today = isoLocal(new Date());
  const fDate = signal(today), fRef = signal(''), fAmount = signal(''), fTax = signal('');
  const fDebitGl = signal(''), fDebitDest = signal('G'), fDebitContact = signal('');
  const fCreditGl = signal(''), fCreditDest = signal('G'), fCreditContact = signal('');
  const fDebitDesc = signal(''), fCreditDesc = signal('');
  const fAutoRef = signal(false), fKeepDesc = signal(true);
  const saving = signal(false), formErr = signal('');
  // Clear the validation banner the moment the user starts fixing a field, rather than leaving it up
  // until the next Add. Mirrors the invoice editor's per-field clearInvalid (see CLAUDE.md).
  const clearFormErr = () => { if(formErr.value) formErr.value = ''; };
  // Select-on-focus: clicking into a field highlights its whole current value so the user overwrites
  // without backspacing (owner 2026-09-17). Guarded — <input type=number> and <sleek-date> expose no
  // usable text selection, so select() may be absent or throw; never let that break focus.
  const selectOnFocus = e => { try { e.target.select && e.target.select(); } catch { /* nothing to select */ } };

  const confirmId = signal(null), deleting = signal(false), delErr = signal('');
  const postAsk = signal(false), posting = signal(false);
  // inline edit of a staged entry — plain vars so typing never re-renders (keeps caret). The two GL
  // accounts are editable via searchable pickers (eDebitGl/eCreditGl hold the picked ids); the tax
  // picker is searchable too (eTax id + eTaxLabel display).
  const editId = signal(0), editErr = signal(''), editSaving = signal(false);
  let eDate = '', eRef = '', eAmount = '', eDesc = '', eContra = '', eTax = '', eTaxLabel = '';
  let eDebitGl = '', eDebitLabel = '', eCreditGl = '', eCreditLabel = '', eDebitIsGl = false, eCreditIsGl = false;
  const taxLabel = id => {
    if(!id) return t('journals.no_tax', 'No tax');
    const tc = (((reference.value || {}).tax_codes) || []).find(x => String(x.id) === String(id));
    return tc ? (tc.code + ' · ' + tc.name) : t('journals.no_tax', 'No tax');
  };
  function startEdit(r){
    editErr.value = '';
    eDate = r.date; eRef = r.reference || ''; eAmount = String(r.amount);
    eDesc = r.description || ''; eContra = r.contra_description || '';
    eTax = r.tax_code_id ? String(r.tax_code_id) : ''; eTaxLabel = taxLabel(eTax);
    const dl = r.debit || {}, cl = r.credit || {};
    // GL legs get an editable searchable picker (seeded to the current account); a contact (Debtor/
    // Creditor) leg has no picker and is preserved by the API (change those via delete + re-add).
    eDebitIsGl = !!(dl.account_id && !dl.contact_id);
    eCreditIsGl = !!(cl.account_id && !cl.contact_id);
    eDebitGl = eDebitIsGl ? String(dl.account_id) : '';
    eDebitLabel = eDebitIsGl ? (formatGlCode(dl.code) + ' — ' + (dl.name || '')) : '';
    eCreditGl = eCreditIsGl ? String(cl.account_id) : '';
    eCreditLabel = eCreditIsGl ? (formatGlCode(cl.code) + ' — ' + (cl.name || '')) : '';
    editId.value = r.id;
  }
  function cancelEdit(){ editId.value = 0; editErr.value = ''; }
  async function saveEdit(id){
    const amt = parseFloat(eAmount);
    if(!eDate){ editErr.value = t('journals.pick_date', 'Pick a date.'); return; }
    if(!amt){ editErr.value = t('journals.enter_amount', 'Enter an amount.'); return; }
    if(!eDesc.trim()){ editErr.value = t('journals.enter_debit_desc', 'Enter a debit description.'); return; }
    if(eDebitIsGl && !eDebitGl){ editErr.value = t('journals.pick_debit_gl', 'Pick a debit GL account.'); return; }
    if(eCreditIsGl && !eCreditGl){ editErr.value = t('journals.pick_credit_gl', 'Pick a credit GL account.'); return; }
    editSaving.value = true; editErr.value = '';
    try {
      const body = { date: eDate, amount: amt, description: eDesc.trim(),
        contra_description: eContra.trim() || undefined, reference: eRef.trim() || undefined,
        tax_code_id: eTax ? Number(eTax) : undefined };
      // GL legs send their (possibly changed) account with dest G; contact legs send nothing → preserved.
      if(eDebitGl){ body.debit_account_id = Number(eDebitGl); body.debit_dest = 'G'; }
      if(eCreditGl){ body.credit_account_id = Number(eCreditGl); body.credit_dest = 'G'; }
      const res = await api.post('/companies/' + companyId + '/journals/' + id + '/update', body);
      // the amount in the company's own currency (the API's text assumes the rand)
      toast(res && res.success !== false
        ? t('journals.entry_updated_amt', 'Journal entry of {amt} updated').replace('{amt}', fmtR(amt))
        : t('journals.entry_updated', 'Journal entry updated'));
      editId.value = 0;
      await load();
    } catch(e){ editErr.value = (e.data && e.data.error) || e.message || t('journals.update_failed', 'Update failed'); }
    editSaving.value = false;
  }

  async function load(){
    try {
      const r = await api.get('/companies/' + companyId + '/journals');
      rows.value = r.data || [];
      err.value = '';
    } catch(e){ err.value = (e.data && e.data.error) || t('journals.err_load', 'Could not load journal entries'); }
    loading.value = false;
  }
  load();
  (async () => {
    try {
      reference.value = await api.get('/companies/' + companyId + '/bank-reference');
      configureSleekDate(reference.value);
      seedTax();                                    // fill + reveal the tax picker (VAT-registered only)
    } catch {}
  })();

  // Journals rework (Andre 2026-09-17): this screen is the CAPTURE FORM + the UNPOSTED staged
  // entries only. The old "journal register" (every entry forever, with All/Unposted/Posted filter
  // + infinite scroll) was removed — posted journals live in the ledger/reports, not here. GET
  // /companies/{id}/journals returns unposted entries only, so posting clears them from this list.

  const gcode = code => formatGlCode(code);
  const dmy = iso => formatDate(iso, (reference.value || {}).date_format);
  // v2 allows ALL GL here — cashbooks (transfers) and control accounts included
  const glAccounts = () => ((reference.value && reference.value.accounts) || [])
    .filter(a => a.is_postable && a.code !== '0000.000' && a.code !== '0000');
  const contacts = () => ((reference.value && reference.value.contacts) || []);
  const taxCodes = () => ((reference.value && reference.value.tax_codes) || []);
  // the VAT flag has teeth: not registered → no tax select, no V destination
  const vatOn = () => !!(reference.value && reference.value.vat_registered);

  function toast(m){ notice.value = m; setTimeout(() => { if(notice.value === m) notice.value = ''; }, 4000); }
  const legLabel = leg => !leg ? '' : (leg.contact || (gcode(leg.code) + ' ' + (leg.name || '')));
  const needsContact = d => d === 'D' || d === 'C';

  // ── v2 keyboard flow: JN_FIELD_ORDER + Enter-advance (Shift+Enter back, last = Add) ──
  // Date → Ref → Amount → Debit pick → Credit pick → Debit desc → Credit desc → Add.
  // Hidden fields are skipped; the GL/contact typeaheads swallow Enter while their
  // dropdown is open (Enter there picks the highlighted row instead).
  const FIELD_ORDER = ['jrnDate', 'jrnRef', 'jrnAmount', 'jrnDebitPick', 'jrnCreditPick',
                       'jrnDebitDesc', 'jrnCreditDesc'];
  function fieldEl(key){
    if(key === 'jrnDebitPick')
      return document.getElementById(needsContact(fDebitDest.value) ? 'jrnDebitContact' : 'jrnDebitGl');
    if(key === 'jrnCreditPick')
      return document.getElementById(needsContact(fCreditDest.value) ? 'jrnCreditContact' : 'jrnCreditGl');
    return document.getElementById(key);
  }
  function focusField(key){
    const el = fieldEl(key);
    if(!el) return;
    el.focus();
    try { el.select && el.select(); } catch { /* sleek-date has no select() */ }
  }
  function advance(fromKey, back){
    const idx = FIELD_ORDER.indexOf(fromKey);
    let i = idx + (back ? -1 : 1);
    while(i >= 0 && i < FIELD_ORDER.length){
      const el = fieldEl(FIELD_ORDER[i]);
      if(el && el.offsetParent !== null && !el.disabled){ focusField(FIELD_ORDER[i]); return; }
      i += back ? -1 : 1;
    }
  }
  function metaKeydown(e, key){
    if(e.key !== 'Enter') return;
    e.preventDefault();
    if(e.shiftKey) return advance(key, true);
    if(key === 'jrnCreditDesc'){ const b = document.getElementById('jrnAddBtn'); b && b.click(); return; }
    advance(key);
  }

  // ── searchable GL / contact typeaheads (imperative dropdown — focus survives) ──
  const floatRoot = mountFloatRoot();   // shared layer — ONE document listener app-wide
  const closeFloat = closeFloatRoot;
  function positionNear(el, anchor, width){
    const r = anchor.getBoundingClientRect();
    el.style.position = 'fixed';
    el.style.left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) + 'px';
    el.style.zIndex = 300;
    floatRoot.appendChild(el);
    const h = el.offsetHeight, below = window.innerHeight - r.bottom - 8;
    el.style.top = (below >= h ? r.bottom + 4 : Math.max(8, r.top - h - 4)) + 'px';
  }
  let dropEl = null, dropCount = 0;
  function renderDrop(anchor, rows){
    closeFloat();
    dropEl = document.createElement('div');
    dropEl.className = 'cb-dropdown';
    dropCount = rows.length;
    if(!rows.length) dropEl.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>';
    rows.forEach(([label, onPick]) => {
      const item = document.createElement('div');
      item.className = 'cb-drop-item';
      item.innerHTML = label;
      item.onclick = e => { e.stopPropagation(); onPick(); };
      dropEl.appendChild(item);
    });
    positionNear(dropEl, anchor, 350);
  }
  function showGlDrop(anchor, side){
    const q = anchor.value.trim().toLowerCase();
    if(!q){ closeFloat(); dropCount = 0; return; }
    const ref = reference.value || {};
    const rows = glAccounts()
      .filter(a => String(a.code).toLowerCase().includes(q) || String(a.name || '').toLowerCase().includes(q))
      .slice(0, 12).map(a => {
        const tag = controlTag(a);
        const bal = (ref.balances || {})[String(a.id)] || 0;
        return [`<span><span class="mono">${gcode(a.code)}</span> ${a.name}` +
          (tag ? ` <span class="cb-ctl-tag cb-ctl-${tag.toLowerCase()}">${tag}</span>` : '') +
          `</span><span class="mono cb-drop-bal">${fmtRS(bal)}</span>`,
          () => pickGlAcct(side, a, anchor)];
      });
    renderDrop(anchor, rows);
  }
  function showContactDrop(anchor, side){
    const q = anchor.value.trim().toLowerCase();
    const dest = side === 'debit' ? fDebitDest.value : fCreditDest.value;
    const pool = contacts().filter(c => (dest === 'D' ? c.is_customer : c.is_supplier) &&
      (!q || String(c.code || '').toLowerCase().includes(q) || String(c.name || '').toLowerCase().includes(q)));
    const rows = pool.slice(0, 12).map(c => [
      `<span><span class="mono">${c.code || ''}</span> ${c.name}</span>` +
      `<span class="mono cb-drop-bal ${Number(c.balance || 0) < 0 ? 't-out' : 't-in'}">${fmtRS(c.balance || 0)}</span>`,
      () => pickContactSide(side, c, anchor)]);
    renderDrop(anchor, rows);
  }
  // Default-contra auto-fill (Andre 2026-09-17): if the picked account has a default contra set in
  // its setup (Chart of Accounts → accounting.account.contra_account_id, surfaced on the reference),
  // fill the OPPOSITE leg's GL with it — but only when that leg is still on a GL destination and
  // empty, so a user's own pick is never overwritten.
  function autofillContra(side, account){
    const contraId = account && account.contra_account_id;
    if(!contraId) return;
    const otherDest = side === 'debit' ? fCreditDest : fDebitDest;
    const otherGl = side === 'debit' ? fCreditGl : fDebitGl;
    if(otherDest.value !== 'G' || otherGl.value) return;         // never clobber a non-GL or filled leg
    const contra = glAccounts().find(a => String(a.id) === String(contraId));
    if(!contra) return;                                          // contra not a postable GL here → skip
    otherGl.value = String(contra.id);
    const el = document.getElementById(side === 'debit' ? 'jrnCreditGl' : 'jrnDebitGl');
    if(el) el.value = gcode(contra.code) + ' — ' + contra.name;  // reflect it in the uncontrolled input
  }
  function pickGlAcct(side, account, anchor){
    const label = gcode(account.code) + ' — ' + account.name;
    clearFormErr();
    if(side === 'debit'){
      fDebitGl.value = String(account.id);
      // v2: picking the debit account copies its name into the debit description
      if(!fDebitDesc.value.trim()) fDebitDesc.value = account.name;
    } else fCreditGl.value = String(account.id);
    anchor.value = label;
    autofillContra(side, account);
    closeFloat(); dropCount = 0;
    advance(side === 'debit' ? 'jrnDebitPick' : 'jrnCreditPick');
  }
  function pickContactSide(side, contact, anchor){
    clearFormErr();
    if(side === 'debit') fDebitContact.value = String(contact.id);
    else fCreditContact.value = String(contact.id);
    anchor.value = (contact.code ? contact.code + ' — ' : '') + contact.name;
    closeFloat(); dropCount = 0;
    advance(side === 'debit' ? 'jrnDebitPick' : 'jrnCreditPick');
  }
  function pickKeydown(e, side){
    const open = dropEl && floatRoot.contains(dropEl);
    if(open){
      if(e.key === 'Escape'){ closeFloat(); dropCount = 0; return; }
      if(e.key === 'ArrowDown' || e.key === 'ArrowUp'){
        dropdownKeydown(e, dropEl, { wrap: false }); return;
      }
      if(e.key === 'Enter' || e.key === 'Tab'){
        const current = dropEl.querySelector('.cb-drop-item.highlighted');
        const items = dropEl.querySelectorAll('.cb-drop-item:not(.cb-drop-muted)');
        // v2: Tab/Enter auto-pick only when exactly one match; Enter picks the highlight
        if(current){ e.preventDefault(); current.click(); return; }
        if(items.length === 1){ e.preventDefault(); items[0].click(); return; }
        if(e.key === 'Enter'){ e.preventDefault(); return; }   // several matches: wait for arrows
        closeFloat(); dropCount = 0;                           // Tab moves on
        return;
      }
      return;
    }
    metaKeydown(e, side === 'debit' ? 'jrnDebitPick' : 'jrnCreditPick');
  }

  // ── inline-editor pickers: the SAME imperative float-dropdown the capture form uses, wired to the
  // edit's plain-let vars. Focus-safe, searchable, and survives a list() re-render because the inputs
  // live in the editRow template and re-apply .value=${eLabel}. ──
  function showEditGlDrop(anchor, which){
    const q = anchor.value.trim().toLowerCase();
    if(!q){ closeFloat(); dropCount = 0; return; }
    const ref = reference.value || {};
    const rows = glAccounts()
      .filter(a => String(a.code).toLowerCase().includes(q) || String(a.name || '').toLowerCase().includes(q))
      .slice(0, 12).map(a => {
        const tag = controlTag(a);
        const bal = (ref.balances || {})[String(a.id)] || 0;
        return [`<span><span class="mono">${gcode(a.code)}</span> ${a.name}` +
          (tag ? ` <span class="cb-ctl-tag cb-ctl-${tag.toLowerCase()}">${tag}</span>` : '') +
          `</span><span class="mono cb-drop-bal">${fmtRS(bal)}</span>`,
          () => pickEditGl(which, a, anchor)];
      });
    renderDrop(anchor, rows);
  }
  function pickEditGl(which, account, anchor){
    const label = gcode(account.code) + ' — ' + account.name;
    if(which === 'debit'){ eDebitGl = String(account.id); eDebitLabel = label; }
    else { eCreditGl = String(account.id); eCreditLabel = label; }
    anchor.value = label;
    closeFloat(); dropCount = 0;
  }
  function showEditTaxDrop(anchor){
    const q = anchor.value.trim().toLowerCase();
    const opts = [{ id: '', code: '', name: t('journals.no_tax', 'No tax') }, ...taxCodes()];
    const rows = opts
      .filter(o => !q || String(o.code || '').toLowerCase().includes(q) || String(o.name || '').toLowerCase().includes(q))
      .slice(0, 12).map(o => [
        o.id ? `<span><span class="mono">${o.code}</span> ${o.name}</span>` : `<span>${o.name}</span>`,
        () => { eTax = o.id ? String(o.id) : ''; eTaxLabel = o.id ? (o.code + ' · ' + o.name) : t('journals.no_tax', 'No tax');
                anchor.value = eTaxLabel; closeFloat(); dropCount = 0; }]);
    renderDrop(anchor, rows);
  }
  function editPickKeydown(e){
    const open = dropEl && floatRoot.contains(dropEl);
    if(!open){ if(e.key === 'Enter') e.preventDefault(); return; }   // never submit on Enter
    if(e.key === 'Escape'){ closeFloat(); dropCount = 0; return; }
    if(e.key === 'ArrowDown' || e.key === 'ArrowUp'){ dropdownKeydown(e, dropEl, { wrap: false }); return; }
    if(e.key === 'Enter' || e.key === 'Tab'){
      const current = dropEl.querySelector('.cb-drop-item.highlighted');
      const items = dropEl.querySelectorAll('.cb-drop-item:not(.cb-drop-muted)');
      if(current){ e.preventDefault(); current.click(); return; }
      if(items.length === 1){ e.preventDefault(); items[0].click(); return; }
      if(e.key === 'Enter') e.preventDefault();
    }
  }

  async function addEntry(e){
    e.preventDefault();
    const amt = parseFloat(fAmount.value);
    if(!fDate.value){ formErr.value = t('journals.pick_date', 'Pick a date.'); return; }
    if(!amt){ formErr.value = t('journals.enter_amount', 'Enter an amount.'); return; }
    if(!fDebitDesc.value.trim()){ formErr.value = t('journals.enter_debit_desc', 'Enter a debit description.'); return; }
    if(fDebitDest.value === 'G' && !fDebitGl.value){ formErr.value = t('journals.pick_debit_gl', 'Pick a debit GL account.'); return; }
    if(needsContact(fDebitDest.value) && !fDebitContact.value){ formErr.value = t('journals.pick_debit_contact', 'Pick a contact for the debit side.'); return; }
    if(fCreditDest.value === 'G' && !fCreditGl.value){ formErr.value = t('journals.pick_credit_gl', 'Pick a credit GL account.'); return; }
    if(needsContact(fCreditDest.value) && !fCreditContact.value){ formErr.value = t('journals.pick_credit_contact', 'Pick a contact for the credit side.'); return; }
    saving.value = true; formErr.value = '';
    try {
      const res = await api.post('/companies/' + companyId + '/journals', {
        date: fDate.value, amount: amt, reference: fRef.value.trim() || undefined,
        description: fDebitDesc.value.trim(),
        contra_description: fCreditDesc.value.trim() || undefined,
        debit_dest: fDebitDest.value,
        debit_account_id: fDebitDest.value === 'G' ? Number(fDebitGl.value) : undefined,
        debit_contact_id: needsContact(fDebitDest.value) ? Number(fDebitContact.value) : undefined,
        credit_dest: fCreditDest.value,
        credit_account_id: fCreditDest.value === 'G' ? Number(fCreditGl.value) : undefined,
        credit_contact_id: needsContact(fCreditDest.value) ? Number(fCreditContact.value) : undefined,
        tax_code_id: fTax.value ? Number(fTax.value) : undefined,
      });
      // the amount in the company's own currency (the API's text assumes the rand)
      toast(res && res.success !== false
        ? t('journals.entry_staged_amt', 'Journal entry of {amt} staged').replace('{amt}', fmtR(amt))
        : t('journals.entry_staged', 'Journal entry staged'));
      if(fAutoRef.value && fRef.value.trim()) fRef.value = incrementRef(fRef.value.trim());   // live auto-increment
      if(!fKeepDesc.value){ fDebitDesc.value = ''; fCreditDesc.value = ''; }                  // live keep-description
      // Every add starts a FRESH double-entry: clear BOTH GL sides — the signals AND the uncontrolled
      // picker inputs — and reset the destinations to General (a fresh row's default). "Keep
      // description" (above) keeps ONLY the two descriptions; it must NEVER carry the GL accounts to
      // the next entry (owner 2026-09-17).
      fDebitGl.value = ''; fCreditGl.value = ''; fDebitContact.value = ''; fCreditContact.value = '';
      fDebitDest.value = 'G'; fCreditDest.value = 'G';
      ['jrnDebitGl', 'jrnCreditGl', 'jrnDebitContact', 'jrnCreditContact'].forEach(id => {
        const el = document.getElementById(id); if(el) el.value = '';
      });
      fAmount.value = '';
      await load();
      setTimeout(() => focusField('jrnDate'), 80);   // v2: capture continues from the date
    } catch(e2){ formErr.value = (e2.data && e2.data.error) || e2.message || t('journals.stage_failed', 'Could not stage the entry'); }
    saving.value = false;
  }

  function armDelete(id){ delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try { await api.post('/companies/' + companyId + '/journals/' + id + '/delete', {}); confirmId.value = null; await load(); }
    catch(e){ delErr.value = (e.data && e.data.error) || t('journals.delete_failed', 'Delete failed'); }
    deleting.value = false;
  }
  async function doPost(){
    posting.value = true;
    try {
      const res = await api.post('/companies/' + companyId + '/journals/post', {});
      postAsk.value = false;
      toast(res.message || t('journals.post_success', 'Posted to the ledger'));
      await load();
      // refresh the reference so posted balances (and thus any new entry's projected balance) are current
      try { reference.value = await api.get('/companies/' + companyId + '/bank-reference'); } catch { /* keep the old reference */ }
    } catch(e){ toast((e.data && e.data.error) || t('journals.post_failed', 'Could not post the entries')); }
    posting.value = false;
  }

  // ONE reactive block per pill row (signals read at the top, plain values inside);
  // V (VAT control) only exists for a VAT-registered company — the flag has teeth.
  const destPills = side => {
    const destSig = side === 'debit' ? fDebitDest : fCreditDest;
    const active = destSig.value;
    const vals = vatOn() ? ['G', 'D', 'C', 'V'] : ['G', 'D', 'C'];
    return vals.map(val => html`<button type="button"
      class=${'jrn-dest' + (active === val ? ' active' : '')}
      title=${val === 'G' ? t('journals.dest_g', 'General GL') : val === 'D' ? t('journals.dest_d', 'Debtor (contact)') : val === 'C' ? t('journals.dest_c', 'Creditor (contact)') : t('journals.dest_v', 'VAT control')}
      @click=${() => {
        destSig.value = val;
        if(side === 'debit'){ fDebitGl.value = ''; fDebitContact.value = ''; }
        else { fCreditGl.value = ''; fCreditContact.value = ''; }
        ['jrn' + (side === 'debit' ? 'Debit' : 'Credit') + 'Gl',
         'jrn' + (side === 'debit' ? 'Debit' : 'Credit') + 'Contact'].forEach(id => {
          const el = document.getElementById(id); if(el) el.value = '';
        });
        closeFloat();
        setTimeout(() => focusField(side === 'debit' ? 'jrnDebitPick' : 'jrnCreditPick'), 40);
      }}>${val}</button>`);
  };

  // Projected balance chip for one leg (Andre 2026-09-17: "if I post this, this account's new
  // balance is R…"). A GL leg (bank GLs included) shows the ACCOUNT's own projected balance from its
  // year_balance — the account's figure in the Trial Balance of the year the entry is DATED in (owner
  // 2026-09-30), sent by the API per leg; ref.balances (this year's) only when the API sends none. A
  // Debtor/Creditor leg shows the CONTACT's projected balance. Natural terms +
  // right-aligned tabular (the "basics" gate — numbers never left-aligned). ref is read at the top
  // of list() so this stays a plain call, never a nested reactive slot.
  const projChip = (leg, ref) => {
    if(!leg || !ref) return '';
    let p;
    if(leg.contact_id){                                          // D/C leg → the CONTACT's balance
      const c = (ref.contacts || []).find(x => String(x.id) === String(leg.contact_id));
      if(!c) return '';
      // A contact's balance is a POSITIVE magnitude of what's owed (open_item.outstanding = total ×
      // doc-sign — a debtor's is what they owe us, a creditor's what we owe them; NOT the ledger's
      // debit-positive convention). A debit raises a debtor / lowers a creditor; a credit is the
      // opposite — so the leg's signed delta flips for a credit-normal (creditor) control account.
      const factor = leg.normal_side === 'credit' ? -1 : 1;
      p = Math.round((Number(c.balance || 0) + factor * Number(leg.amount || 0)) * 100) / 100;
    } else if(leg.account_id != null){                           // GL leg (incl. bank) → account balance
      const base = leg.year_balance != null ? leg.year_balance : (ref.balances || {})[String(leg.account_id)];
      p = projectedBalance(base, leg.amount, leg.normal_side);
    } else return '';
    if(p === 0) p = 0;                                           // never render -0
    // Projected balance shown GREEN for both legs (Andre 2026-09-17 huddle: red read as "broken" on a
    // normal journal). The t-in/t-out class still tracks leg direction (debit +, credit −) but both now
    // resolve to --ok; kept as classes in case a value-based colour is wanted later.
    const dir = leg.amount > 0 ? ' t-in' : leg.amount < 0 ? ' t-out' : '';
    return html`<span class=${'jrn-proj mono' + dir} title=${t('journals.proj_hint', "This account's balance after you post this entry")}>→ ${fmtRS(p)}</span>`;
  };

  // A multi-line entry (made over the MCP — a pay run, opening balances: partner feedback 2026-10-05)
  // lists every debit line under Debit and every credit line under Credit, each with its amount. It has
  // no inline edit (the editor is one debit + one credit) — delete and re-make it while staged.
  const multiLegs = r => {
    const side = sign => (r.lines || []).filter(l => sign > 0 ? l.debit > 0 : l.credit > 0).map(l => html`
      <div class="jrn-multi-line"><span class="jrn-acct-name">${legLabel({ ...l, contact: l.contact_name })}</span>
        <span class="mono jrn-multi-amt">${fmtR(sign > 0 ? l.debit : l.credit)}</span></div>`);
    return html`<td class="jrn-acct" title=${t('journals.multi_hint', 'A multi-line entry — delete and re-make it to change it')}>${side(1)}</td>
      <td class="jrn-acct">${side(-1)}</td>`;
  };

  const list = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const data = rows.value, note = notice.value, ref = reference.value;
    const confirming = confirmId.value, busyDel = deleting.value, delNote = delErr.value;
    const ask = postAsk.value, postBusy = posting.value;
    const editing = editId.value, eBusy = editSaving.value, eNote = editErr.value;
    const pastOk = isCompanyAdmin(coRole.value) ? 'true' : 'false';   // earlier FYs: Owners/Admins only
    const acctFixed = t('journals.acct_fixed', 'This leg posts to a contact — change it via delete + re-add');
    // ONE clean inline editor row on a single line (Andre 2026-09-17), aligned to the display columns.
    // Date · editable Debit GL · editable Credit GL · two descriptions side-by-side · Ref · compact
    // amount+tax side-by-side · ✓/✕. Plain-let vars keep the caret; the GL/tax pickers are the same
    // searchable float-dropdown as the capture form. A contact (D/C) leg has no GL picker — it shows a
    // fixed label and is preserved.
    const editGl = (which, leg, label) => (leg && leg.contact_id)
      ? html`<span class="jrn-edit-fixed" title=${acctFixed}>${legLabel(leg)}</span>`
      : html`<input class="form-control jrn-edit-in mono" .value=${label}
          @input=${e => { if(which === 'debit'){ eDebitGl = ''; eDebitLabel = e.target.value; } else { eCreditGl = ''; eCreditLabel = e.target.value; } showEditGlDrop(e.target, which); }}
          @focus=${e => { selectOnFocus(e); if(e.target.value) showEditGlDrop(e.target, which); }}
          @keydown=${editPickKeydown}
          placeholder=${t('journals.gl_ph_short', 'GL — code or name')} aria-label=${which === 'debit' ? 'Debit account' : 'Credit account'}>`;
    const editRow = r => html`<tr class="jrn-edit-row">
      <td><sleek-date class="jrn-edit-date" data-past=${pastOk} .value=${eDate} @focus=${selectOnFocus} @change=${e => { eDate = e.target.value; }} aria-label=${t('common.date', 'Date')}></sleek-date></td>
      <td>${editGl('debit', r.debit, eDebitLabel)}</td>
      <td>${editGl('credit', r.credit, eCreditLabel)}</td>
      <td><div class="jrn-edit-inline">
        <input class="form-control jrn-edit-in" .value=${eDesc} @input=${e => { eDesc = e.target.value; }} @focus=${selectOnFocus} placeholder=${t('journals.debit_desc_short', 'Debit description')} aria-label="Debit description">
        <input class="form-control jrn-edit-in" .value=${eContra} @input=${e => { eContra = e.target.value; }} @focus=${selectOnFocus} placeholder=${t('journals.credit_desc_short', 'Credit description')} aria-label="Credit description">
      </div></td>
      <td><input class="form-control jrn-edit-in mono" .value=${eRef} @input=${e => { eRef = e.target.value; }} @focus=${selectOnFocus} placeholder=${t('common.ref', 'Ref')} aria-label="Reference"></td>
      <td><div class="jrn-edit-inline jrn-edit-amtcell">
        ${vatOn() ? html`<input class="form-control jrn-edit-in jrn-edit-tax mono" .value=${eTaxLabel}
          @input=${e => { eTax = ''; eTaxLabel = e.target.value; showEditTaxDrop(e.target); }}
          @focus=${e => { selectOnFocus(e); showEditTaxDrop(e.target); }} @keydown=${editPickKeydown} placeholder=${t('journals.no_tax', 'No tax')} aria-label="Tax code">` : ''}
        <input type="number" step="0.01" min="0" class="form-control jrn-edit-in jrn-edit-amt text-right mono" .value=${eAmount}
          @input=${e => { eAmount = e.target.value; }} @focus=${selectOnFocus}
          @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); saveEdit(r.id); } else if(e.key === 'Escape'){ cancelEdit(); } }} aria-label="Amount">
      </div></td>
      <td class="cb-td-act jrn-edit-act">
        <span class="jrn-edit-btns">
          <button type="button" class="icon-btn jrn-edit-ok" title=${t('common.save', 'Save')} ?disabled=${eBusy} @click=${() => saveEdit(r.id)}>${eBusy ? '…' : '✓'}</button>
          <button type="button" class="icon-btn jrn-edit-cancel" title=${t('common.cancel', 'Cancel')} @click=${cancelEdit}>✕</button>
        </span>
        ${eNote ? html`<span class="row-err jrn-edit-err">${eNote}</span>` : ''}
      </td>
    </tr>`;
    const total = data.reduce((s, r) => s + Number(r.amount || 0), 0);
    // v2 layout: a small Post button lives in the staged-entries header (top right)
    // and in the footer bar; arming the confirm swaps the FOOTER one for the
    // inline confirm and hides the header one — never two live buttons.
    const postBtn = () => html`<button type="button" class="btn btn-primary btn-sm"
      @click=${() => { postAsk.value = true; }}>${t('common.post_to_ledger', 'Post to Ledger')}</button>`;
    const confirmStrip = () => html`<span class="confirm-inline confirm-go"><span class="confirm-q">${t('journals.post_confirm', 'Post all journal entries to the ledger? This cannot be undone.')}</span>
      <button type="button" class="btn-confirm-yes" ?disabled=${postBusy} @click=${doPost}>${postBusy ? t('common.posting', 'Posting…') : t('common.confirm', 'Confirm')}</button>
      <button type="button" class="btn-confirm-no" @click=${() => { postAsk.value = false; }}>${t('common.cancel', 'Cancel')}</button></span>`;
    return html`<div>
      ${note ? html`<div class="alert alert-ok py-2 mb-2">${note}</div>` : ''}
      ${data.length ? html`<div class="jrn-staged-head">
        <span class="jrn-staged-title">${t('journals.staged_entries', 'Staged entries')}</span>
        <span class="cb-tab-badge">${data.length}</span>
        ${ask ? '' : postBtn()}
      </div>` : ''}
      ${data.length ? html`<div class="table-wrap"><table class="cb-table jrn-table">
        <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.debit', 'Debit')}</th><th>${t('common.credit', 'Credit')}</th><th>${t('common.description', 'Description')}</th><th>${t('common.ref', 'Ref')}</th>
          <th class="text-right">${t('common.amount', 'Amount')}</th><th></th></tr></thead>
        <tbody>${data.map(r => editing === r.id ? editRow(r) : html`<tr>
          <td class="mono cb-td-date">${dmy(r.date)}</td>
          ${r.multi_line ? multiLegs(r) : html`
          <td class="jrn-acct"><span class="jrn-acct-name">${legLabel(r.debit)}</span>${projChip(r.debit, ref)}</td>
          <td class="jrn-acct"><span class="jrn-acct-name">${legLabel(r.credit)}</span>${projChip(r.credit, ref)}</td>`}
          <td>${r.description}</td>
          <td class="mono">${r.reference}</td>
          <td class="mono text-right">${fmtR(r.amount)}</td>
          <td class="cb-td-act">
            ${r.locked
              ? html`<span class="row-lock row-lock-red" title=${t('journals.locked_hint', "This entry's date is in a locked financial year — see Financial Years (top-right corner)")}>🔒</span>`
              : html`${r.multi_line ? '' : html`<button type="button" class="icon-btn jrn-edit-btn" title=${t('journals.edit_hint', 'Edit this entry')} ?disabled=${editing && editing !== r.id} @click=${() => startEdit(r)}>✎</button>`}
            ${rowDelete({ armed: confirming === r.id, busy: busyDel,
              error: confirming === r.id ? delNote : '', label: t('journals.delete_confirm', 'Delete this entry?'),
              onArm: () => armDelete(r.id), onCancel: cancelDelete, onDelete: () => doDelete(r.id) })}`}</td>
        </tr>`)}</tbody>
        <tfoot><tr class="jrn-total"><td colspan="5" class="text-right fw-700">${t('common.total', 'Total')}</td>
          <td class="mono text-right fw-700">${fmtR(total)}</td><td></td></tr></tfoot>
      </table></div>
      <div class="jrn-postbar">${ask ? confirmStrip() : postBtn()}</div>`
      : html`<div class="muted-block">${t('journals.empty_staged', 'No staged journal entries — capture one above.')}</div>`}
    </div>`;
  };

  const content = html`<div class="page page-wide">
    <div class="page-head"><div><h1 class="page-title">${() => t('journals.title', 'Journals')}</h1>
      <div class="page-sub">${() => t('journals.instruction', 'Double-entry journal — debit one account, credit another.')}</div></div></div>

    <form class="jrn-cap mb-2" @submit=${addEntry}>
      <div class="jrn-form">
        <div class="jrn-meta">
          <sleek-date id="jrnDate" data-next="#jrnRef" .value=${fDate}
            data-past=${() => isCompanyAdmin(coRole.value) ? 'true' : 'false'}
            @keydown=${e => metaKeydown(e, 'jrnDate')}
            @change=${e => { clearFormErr(); fDate.value = e.target.value; }}></sleek-date>
          <input id="jrnRef" class="form-control jrn-ref" aria-label="Reference" .value=${fRef}
            @input=${e => { fRef.value = e.target.value; }} @keydown=${e => metaKeydown(e, 'jrnRef')}
            @focus=${selectOnFocus} placeholder=${t('journals.ref_ph', 'Ref e.g. JNL001')}>
          <input id="jrnAmount" class="form-control jrn-amt text-right mono" type="number" step="0.01" min="0" aria-label="Amount"
            .value=${fAmount} @input=${e => { clearFormErr(); fAmount.value = e.target.value; }}
            @keydown=${e => metaKeydown(e, 'jrnAmount')} @focus=${selectOnFocus} placeholder="0.00">
          <span class="jrn-tax-cell jrn-hide" data-jrn-tax-cell></span>
        </div>

        <div class="jrn-row">
          <div class="jrn-side">
            <div class="jrn-side-hd"><span class="jrn-side-tag jrn-dr">${t('journals.debit_tag', 'DEBIT')}</span>
              <span class="jrn-dests">${() => destPills('debit')}</span></div>
            <input id="jrnDebitGl" type="text" autocomplete="off" data-float-anchor
              class=${() => 'form-control jrn-gl' + (fDebitDest.value === 'G' ? '' : ' jrn-hide')}
              aria-label="Debit GL account" placeholder=${t('journals.debit_gl_ph', 'Debit GL — code or name e.g. 8400 or Rent')}
              @input=${e => { clearFormErr(); fDebitGl.value = ''; showGlDrop(e.target, 'debit'); }}
              @focus=${e => { if(e.target.value && !fDebitGl.value) showGlDrop(e.target, 'debit'); }}
              @keydown=${e => pickKeydown(e, 'debit')}>
            <input id="jrnDebitContact" type="text" autocomplete="off" data-float-anchor
              class=${() => 'form-control jrn-contact' + (needsContact(fDebitDest.value) ? '' : ' jrn-hide')}
              aria-label="Debit contact" placeholder=${t('cashbook.contact_search_ph', 'Search contact… e.g. Mountain')}
              @input=${e => { clearFormErr(); fDebitContact.value = ''; showContactDrop(e.target, 'debit'); }}
              @focus=${e => { if(!fDebitContact.value) showContactDrop(e.target, 'debit'); }}
              @keydown=${e => pickKeydown(e, 'debit')}>
            <input id="jrnDebitDesc" class="form-control jrn-desc" aria-label="Debit description" .value=${fDebitDesc}
              @input=${e => { clearFormErr(); fDebitDesc.value = e.target.value; }} @keydown=${e => metaKeydown(e, 'jrnDebitDesc')}
              placeholder=${t('journals.debit_desc_ph', 'Debit description e.g. Insurance for March')}>
          </div>
          <div class="jrn-side">
            <div class="jrn-side-hd"><span class="jrn-side-tag jrn-cr">${t('journals.credit_tag', 'CREDIT')}</span>
              <span class="jrn-dests">${() => destPills('credit')}</span></div>
            <input id="jrnCreditGl" type="text" autocomplete="off" data-float-anchor
              class=${() => 'form-control jrn-gl' + (fCreditDest.value === 'G' ? '' : ' jrn-hide')}
              aria-label="Credit GL account" placeholder=${t('journals.credit_gl_ph', 'Credit GL — code or name e.g. 9500 or Bank')}
              @input=${e => { clearFormErr(); fCreditGl.value = ''; showGlDrop(e.target, 'credit'); }}
              @focus=${e => { if(e.target.value && !fCreditGl.value) showGlDrop(e.target, 'credit'); }}
              @keydown=${e => pickKeydown(e, 'credit')}>
            <input id="jrnCreditContact" type="text" autocomplete="off" data-float-anchor
              class=${() => 'form-control jrn-contact' + (needsContact(fCreditDest.value) ? '' : ' jrn-hide')}
              aria-label="Credit contact" placeholder=${t('journals.credit_contact_ph', 'Search contact… e.g. Telkom')}
              @input=${e => { clearFormErr(); fCreditContact.value = ''; showContactDrop(e.target, 'credit'); }}
              @focus=${e => { if(!fCreditContact.value) showContactDrop(e.target, 'credit'); }}
              @keydown=${e => pickKeydown(e, 'credit')}>
            <input id="jrnCreditDesc" class="form-control jrn-desc" aria-label="Credit description" .value=${fCreditDesc}
              @input=${e => { fCreditDesc.value = e.target.value; }} @keydown=${e => metaKeydown(e, 'jrnCreditDesc')}
              placeholder=${t('journals.credit_desc_ph', 'Credit description — leave blank to copy debit')}>
          </div>
        </div>

        <div class="jrn-actions">
          <label class="switch"><input type="checkbox" .checked=${fAutoRef}
            @change=${e => { fAutoRef.value = e.target.checked; }}><span class="switch-track"></span> ${t('journals.auto_ref', 'Auto-increment reference')}</label>
          <label class="switch"><input type="checkbox" .checked=${fKeepDesc}
            @change=${e => { fKeepDesc.value = e.target.checked; }}><span class="switch-track"></span> ${t('journals.keep_desc', 'Keep description')}</label>
          <span class="jrn-spacer"></span>
          <button id="jrnAddBtn" type="submit" class="btn btn-primary" ?disabled=${() => saving.value}>${() => saving.value ? t('common.adding', 'Adding…') : '+ ' + t('common.add', 'Add')}</button>
        </div>
        ${() => formErr.value ? html`<div class="row-err">${formErr}</div>` : null}
      </div>
    </form>

    ${list}
  </div>`;

  // Capture-form tax code = a SEARCHABLE combobox (owner rule: structured inputs are a real
  // interface), consistent with the GL/contact pickers. "No tax" is the empty default; the picker
  // is shown only when the company is VAT registered (vatOn — the flag has teeth). Options are
  // seeded from taxCodes() once the reference loads. Built imperatively (searchSelect) so it never
  // trips the reactive-input focus footgun.
  const taxSel = searchSelect({ options: [], value: fTax.value,
    placeholder: t('journals.tax_ph', 'No tax — or search e.g. VAT 15%'),
    ariaLabel: t('journals.tax_code', 'Tax code'), className: 'form-control',
    onSelect: v => { clearFormErr(); fTax.value = v || ''; } });
  const taxCell = content.querySelector('[data-jrn-tax-cell]');
  if(taxCell) taxCell.append(taxSel.el);
  function seedTax(){
    if(!taxCell) return;
    taxSel.setOptions([{ value: '', label: t('journals.no_tax', 'No tax') },
      ...taxCodes().map(tc => ({ value: String(tc.id), label: tc.code + ' · ' + tc.name, keywords: tc.name }))]);
    taxSel.set(fTax.value || '');
    taxCell.classList.toggle('jrn-hide', !vatOn());
  }

  return tenantShell(companyId, t('journals.title', 'Journals'), content);
}
